import { QuestionMarkCircleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import {
  ArrowRightStartOnRectangleIcon,
  ChevronDownIcon,
  Cog8ToothIcon,
  UserIcon,
} from '@heroicons/react/24/solid';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  Link,
} from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import React, { type FC } from 'react';
import { useLocation } from 'react-router-dom';
import { AvatarButton } from '@/primitives/avatar.tsx';
import {
  Navbar,
  NavbarDivider,
  NavbarItem,
  NavbarSection,
  NavbarSpacer,
} from '@/primitives/navbar.tsx';
import { useFetchFileUrlQuery } from '@/slices/file-upload-api-slice.ts';
import logo from '/logo-short.png';
import { useAuth } from '../hooks/auth-provider';
import { useSecurityContext } from '../hooks/security-provider.tsx';
import {
  type RepresentativeModel,
  representativeHasValidSubscription,
} from '../types/representative.type.ts';
import type { StaffMemberModel } from '../types/staff-member.type.ts';
import { UserTypeEnum } from '../types/user-type.ts';
import AccountSwitcher from './account/account-switcher.tsx';
import NotificationBadge from './notifications/notification-badge.tsx';

const LayoutNavBar: FC = () => {
  const { navItems, currentAccount } = useSecurityContext();
  const { user, isImpersonating, stopImpersonating, logout } = useAuth();
  const { pathname } = useLocation();
  const { data: profileFile } = useFetchFileUrlQuery(user?.profilePhotoUrl ?? skipToken);

  const displayRole = () => {
    switch (user?.userType) {
      case UserTypeEnum.Admin:
        return 'MeetingMD Admin';
      case UserTypeEnum.DrugRep:
        return 'Representative';
      case UserTypeEnum.StaffMember: {
        const staffAccount = (user as StaffMemberModel)?.accountSiteStaffMembers.find(
          x => x.accountId === currentAccount?.id,
        );
        return staffAccount?.position ?? staffAccount?.staffRole?.description;
      }
    }
  };

  const tabs = navItems
    .filter(item => !item.group)
    .map(item => ({
      ...item,
      isSelected: pathname.startsWith(item.url),
    }));
  const adminTabs = navItems
    .filter(item => item.group === 'admin')
    .map(item => ({
      ...item,
      isSelected: pathname.startsWith(item.url),
    }));
  const adminTabIsSelected = adminTabs.some(item => item.isSelected);

  const fullAccess =
    [UserTypeEnum.Admin, UserTypeEnum.StaffMember].includes(user?.userType) ||
    isImpersonating ||
    representativeHasValidSubscription(user as RepresentativeModel);

  return (
    <Navbar>
      <img alt='MeetingMD' src={logo} className='w-32' />
      <NavbarDivider className='max-lg:hidden' />
      <NavbarSection className='max-lg:hidden !gap-1'>
        {tabs.map(({ label, url, isSelected }) => (
          <NavbarItem key={label} href={url} target={'_self'} current={isSelected}>
            {label}
          </NavbarItem>
        ))}
        {!!adminTabs.length && (
          <Dropdown>
            <DropdownTrigger>
              <Button
                variant='light'
                size='sm'
                className={adminTabIsSelected ? 'bg-gray-100' : ''}
                startContent={<ShieldCheckIcon className='size-5' />}
                endContent={<ChevronDownIcon className='size-3' />}
              >
                MMD Admin
              </Button>
            </DropdownTrigger>
            <DropdownMenu aria-label='Admin menu'>
              {adminTabs.map(({ label, url, icon }) => (
                <DropdownItem
                  key={label}
                  as={Link}
                  href={url}
                  startContent={
                    <div>{React.cloneElement(icon, { className: 'size-5 text-primary-600' })}</div>
                  }
                >
                  {label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        )}
      </NavbarSection>
      <NavbarSpacer />
      <NavbarSection>
        <div className='hidden md:block pt-2'>
          <AccountSwitcher />
        </div>

        <div className='flex items-center gap-0.5'>
          <Button
            isIconOnly
            as={Link}
            href='/support'
            variant='light'
            radius='lg'
            aria-label='Contact support'
            title='Contact support'
            className='size-7 min-w-7 border border-transparent p-0 text-zinc-500 transition hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700 data-[focus-visible=true]:ring-2 data-[focus-visible=true]:ring-primary-500 dark:text-zinc-400 dark:hover:bg-primary-950/40 dark:hover:text-primary-300'
          >
            <QuestionMarkCircleIcon className='size-[22px]' />
          </Button>
          {fullAccess && <NotificationBadge />}
        </div>
        <Dropdown placement='bottom-end'>
          <DropdownTrigger>
            <Button variant='light' className='p-0 min-w-0 h-auto'>
              <div className='flex items-center gap-2'>
                <AvatarButton
                  className='size-10'
                  src={profileFile}
                  initials={`${user?.firstName?.substring(0, 1)}${user?.lastName?.substring(0, 1)}`}
                />
                <div className='hidden lg:block text-left'>
                  <h6 className='text-sm'>
                    {user?.firstName} {user?.lastName}
                  </h6>
                  <p className='text-xs text-gray-500'>{displayRole()}</p>
                </div>
              </div>
            </Button>
          </DropdownTrigger>
          <DropdownMenu aria-label='User menu' className='min-w-64'>
            {fullAccess && (
              <DropdownSection showDivider>
                <DropdownItem
                  key='profile'
                  as={Link}
                  href='/my-profile'
                  startContent={<UserIcon className='size-4' />}
                >
                  My profile
                </DropdownItem>
                <DropdownItem
                  key='settings'
                  as={Link}
                  href='/settings'
                  startContent={<Cog8ToothIcon className='size-4' />}
                >
                  Settings
                </DropdownItem>
              </DropdownSection>
            )}
            <DropdownSection>
              {isImpersonating && (
                <DropdownItem
                  key='stop-impersonate'
                  startContent={<ArrowRightStartOnRectangleIcon className='size-4' />}
                  onPress={stopImpersonating}
                >
                  Stop Impersonating
                </DropdownItem>
              )}
              {!isImpersonating && (
                <DropdownItem
                  key='sign-out'
                  startContent={<ArrowRightStartOnRectangleIcon className='size-4' />}
                  onPress={logout}
                >
                  Sign Out
                </DropdownItem>
              )}
            </DropdownSection>
          </DropdownMenu>
        </Dropdown>
      </NavbarSection>
    </Navbar>
  );
};

export default LayoutNavBar;
